import { useContext, useState, useRef } from "react";
import { AppContext } from "../App";
import { FiMail, FiMapPin, FiPhone, FiMessageSquare, FiArrowRight, FiClock } from "react-icons/fi";
import ReCAPTCHA from "react-google-recaptcha";
import { callApi } from '../Helpers';

// Asset Imports
import banner1 from "../assets/banner1.jpg";
import { FaWhatsapp } from "react-icons/fa";

export default function ContactUsContent() {
  const appContext = useContext(AppContext);
  
  // Form State
  const [contactInfo, setContactInfo] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  
  // CAPTCHA State & Ref
  const [captchaToken, setCaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);

  // API Call Handler
  const submitInquiry = async () => {
    if (contactInfo && contactInfo.trim().length > 0) {
      if (subject && subject.trim().length > 0) {
        if (message && message.trim().length > 0) {
          if (captchaToken) {
            setLoading(true);
            
            await callApi(
              'public/send-inquiry',
              {
                emailOrPhone: contactInfo,
                subject: subject,
                message: message
              }
            ).then(response => {
              if (response.status === 1) {
                appContext.tellMessage('Successful');
                
                // Clear UI cleanly
                setContactInfo("");
                setSubject("");
                setMessage("");
                setCaptchaToken(null);
                if (recaptchaRef.current) {
                  recaptchaRef.current.reset();
                }
              } else {
                appContext.tellError(response.msg || "Failed to process request");
              }
            }).catch(error => {
               appContext.tellError("Network or server error");
            });

            setLoading(false);
          } else {
            appContext.tellError("Please complete the CAPTCHA verification");
          }
        } else {
          appContext.tellError("Invalid message");
        }
      } else {
        appContext.tellError("Invalid subject");
      }
    } else {
      appContext.tellError("Invalid email or phone number");
    }
  };

  return (
    <>
      
      <section className="cu-section">
        <div className="cu-container">
          
          <header className="cu-header">
            <h2 className="cu-title">Reliable Medical <br/><span className="text-secondary font-bold">Inquiry Channels.</span></h2>
          </header>

          <div className="cu-grid">
            
            {/* Visual Header / Map Alternative */}
            <div className="cu-visual">
              <img src={banner1} className="cu-img" alt="Nanguba Hospital Facility" />
              <div className="cu-img-overlay" />
              
              <div className="cu-glass-card">
                <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                  <FiMapPin size={20} color="var(--clinical-teal)" style={{ flexShrink: 0 }} />
                  <div>
                    <h4 style={{ margin: 0, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.15em', opacity: 0.8 }}>Facility Location</h4>
                    <p style={{ margin: 0, fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.4, marginTop: '4px' }}>
                      Kisasa Plaza, 2nd Floor<br/>
                      Plot No. 43, Block A, Morogoro Road<br/>
                      Kisasa, Dodoma. Postcode 41107, TZ
                    </p>
                  </div>
                </div>
                <p style={{ margin: 0, fontSize: '0.85rem', opacity: 0.9, lineHeight: 1.5 }}>
                  Centrally located for immediate emergency response and outpatient specialty care.
                </p>
              </div>
            </div>

            {/* Centralized Action Cards */}
            <div className="cu-actions-list">
              
              {/* WhatsApp */}
              <a href="https://wa.me/255740009632" target="_blank" rel="noreferrer" className="cu-action-card">
                <div className="cu-icon-box" style={{ color: 'var(--whatsapp-green)' }}>
                  <FaWhatsapp />
                </div>
                <div className="cu-card-meta">
                  <h4>Live Support</h4>
                  <p>WhatsApp Chat</p>
                </div>
                <div className="cu-arrow"><FiArrowRight /></div>
              </a>

              {/* Call - Hospital */}
              <a href="tel:+255740009632" className="cu-action-card">
                <div className="cu-icon-box">
                  <FiPhone />
                </div>
                <div className="cu-card-meta">
                  <h4>Hospital</h4>
                  <p>+255 740 009 632</p>
                </div>
                <div className="cu-arrow"><FiArrowRight /></div>
              </a>

              {/* Call - Pharmacy 
              <a href="tel:+255750380032" className="cu-action-card">
                <div className="cu-icon-box">
                  <FiPhone />
                </div>
                <div className="cu-card-meta">
                  <h4>Pharmacy</h4>
                  <p>+255 750 380 032</p>
                </div>
                <div className="cu-arrow"><FiArrowRight /></div>
              </a>
              */}

              {/* Email */}
              <a href="mailto:info@nangubahealth.com" className="cu-action-card">
                <div className="cu-icon-box">
                  <FiMail />
                </div>
                <div className="cu-card-meta">
                  <h4>Email Correspondence</h4>
                  <p>info@nangubahealth.com</p>
                </div>
                <div className="cu-arrow"><FiArrowRight /></div>
              </a>

              {/* Direct Inquiry Form */}
              <div className="cu-form-container">
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                  <FiMessageSquare size={22} color="var(--clinical-teal)" />
                  <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 800 }}>Direct Inquiry</h3>
                </div>
                
                <div>
                  <input 
                    type="text" 
                    placeholder="Email or Phone Number" 
                    className="cu-input" 
                    value={contactInfo}
                    onChange={(e) => setContactInfo(e.target.value)}
                    required 
                  />
                  <input 
                    type="text" 
                    placeholder="Subject" 
                    className="cu-input" 
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    required 
                  />
                  
                  <textarea
                    placeholder="Your Message..."
                    className="cu-input cu-textarea"
                    maxLength={200}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    required
                  ></textarea>
                  <div className="cu-char-count">{message.length}/200 characters allowed</div>
                  
                  {/* Corrected CAPTCHA Node Wrapper */}
                  <div className="cu-captcha-wrapper">
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey="6LfK8g0tAAAAAPXHp-F3gz12laHqgsHufyxon61O" 
                      onChange={(token) => setCaptchaToken(token)}
                    />
                  </div>

                  {captchaToken && contactInfo && subject && message ? (
                    <button 
                      className="cu-btn" 
                      onClick={submitInquiry}
                      disabled={loading}
                    >
                      {loading ? "Sending..." : "Send Message"} {!loading && <FiArrowRight size={18} />}
                    </button>
                  ) : (
                    <button className="cu-btn-placeholder" disabled>
                      Complete Form & Verification <FiArrowRight size={18} />
                    </button>
                  )}
                </div>
              </div>

              {/* Operating Info Footer */}
              <div className="cu-info-footer" style={{ marginTop: '16px', padding: '0 20px', borderLeft: '3px solid var(--clinical-teal)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', color: 'var(--clinical-teal)', fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase' }}>
                  <FiClock /> Operating Hours
                </div>
                <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--slate-500)', lineHeight: 1.6 }}>
                  Emergency Services: <strong>06:00 - 22:00</strong><br/>
                  Outpatient Services: Monday — Sunday, 06:00 - 22:00
                </p>
              </div>

            </div>
          </div>

        </div>
      </section>
    </>
  );
}